import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { doc, getDoc, setDoc, addDoc, collection, query, where, onSnapshot, deleteDoc, Timestamp, getDocs, writeBatch } from 'firebase/firestore';
// no storage import; we store images as base64 in Firestore
import { encryptMessage, decryptMessage, generateEncryptionKey, generateGibberish } from '../utils/encryption';
import { ArrowLeft, Lock, Unlock, Clock, Trash2, Flag } from 'lucide-react';
import toast from 'react-hot-toast';
import { playBeep } from '../utils/sound';
import './Chat.css';

// Static data moved outside component to prevent recreation
const WEATHER_ALERTS = [
  '⚡ Thunderstorm approaching - take cover!',
  '🌧️ Heavy rainfall expected in your area',
  '🌪️ Strong winds detected - secure loose objects',
  '❄️ Freezing temperatures tonight - bundle up!',
  '🌤️ Clear skies ahead - perfect weather!',
  '🌈 Rainbow spotted after recent showers',
  '🌪️ Tornado watch issued for nearby regions',
  '❄️ Snow flurries possible this evening',
  '🌧️ Flash flood warning - avoid low areas',
  '☀️ Heat wave continues - stay hydrated!'
];

const HEADLINES = [
  // India — Breaking & National
  'India Update: IMD issues weather advisory for multiple states',
  'Breaking: Heavy rainfall alert issued for coastal regions',
  'National Alert: Dense fog expected across north India tomorrow morning',
  'Developing: Temperature drop reported in parts of northern India',
  'IMD Update: Heatwave conditions ease in several districts',

  // India — Government & Policy
  'Government Brief: Cabinet reviews infrastructure project timelines',
  'Parliament Update: Key bills scheduled for discussion this week',
  'Policy Watch: Centre issues fresh guidelines for urban development',
  'Official Statement: No changes announced in fuel price structure',
  'Ministry Update: Nationwide safety audits to be conducted',

  // India — Economy & Markets
  'Market Update: Indian indices trade steady amid mixed global cues',
  'Economy Watch: Rupee holds range against major currencies',
  'Business News: GST collections show stable month-on-month growth',
  'Financial Desk: Banking stocks see selective buying',
  'Trade Update: Export growth remains cautious in current quarter',

  // India — Cities & Local
  'Delhi Update: Air quality improves slightly after overnight showers',
  'Mumbai News: Local trains run on schedule despite heavy rains',
  'Bengaluru Report: Traffic advisories issued due to civic work',
  'Chennai Alert: Waterlogging reported in low-lying areas',
  'Hyderabad Update: Power supply restored after brief disruption',
  'Kolkata Desk: Civic authorities increase monsoon preparedness',

  // India — Sports
  'Cricket Update: Team India begins preparation for upcoming series',
  'IPL Watch: Franchise announces squad changes ahead of next match',
  'Sports India: Selection committee to meet later this week',
  'Badminton News: Indian shuttler advances to quarterfinals',
  'Hockey Update: National team camps begin ahead of tournament',

  // India — Movies & Entertainment
  'Bollywood Desk: New release records steady opening at box office',
  'Film News: Director announces release date for upcoming project',
  'Entertainment India: Trailer launch draws strong audience reaction',
  'Cinema Update: Regional films see increased weekend footfall',
  'OTT India: New original series trends across platforms',

  // India — Public & Civic
  'Public Advisory: Authorities urge caution during monsoon travel',
  'Railway Update: Several trains rescheduled due to weather conditions',
  'Health Desk: Seasonal flu cases remain within expected range',
  'Education News: Universities announce revised examination dates',
  'Civic Notice: Municipal bodies conduct flood preparedness drills'
];


const SOURCES = [
  // Existing
  'Northwind Times',
  'Aurora Daily',
  'Coastal Gazette',
  'Skyline Chronicle',
  'Harbor News Network',
  'Prairie Journal',
  'Sierra Tribune',
  'Metro Weather Watch',
  'Frontier Bulletin',
  'Seabreeze Herald',

  // India — National News
  'NDN India News',
  'Bharat News Network',
  'India Today Desk',
  'National Herald India',
  'Republic News Live',
  'Hindustan Bulletin',
  'The Indian Chronicle',
  'Bharat Times',
  'India Report',

  // India — Regional / City
  'Delhi News Desk',
  'Mumbai Metro News',
  'South India Today',
  'Deccan Daily',
  'Eastern India Report',
  'Western India Times',

  // India — Business / Tech
  'Business India Live',
  'Economic Affairs India',
  'Tech Bharat News',

  // India — Entertainment & Sports
  'Bollywood Today',
  'Cricket Nation India',
  'Sports India Desk'
];


const AUTHORS = [
  'A. Patel', 'J. Smith', 'C. Nguyen', 'M. Garcia', 'L. Chen',
  'R. Singh', 'T. Brown', 'S. Wilson', 'E. Rossi', 'D. Kim'
];

const COUNTRIES = [
  { code: 'US', name: 'United States', emoji: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', emoji: '🇬🇧' },
  { code: 'CA', name: 'Canada', emoji: '🇨🇦' },
  { code: 'AU', name: 'Australia', emoji: '🇦🇺' },
  { code: 'IN', name: 'India', emoji: '🇮🇳' },
  { code: 'DE', name: 'Germany', emoji: '🇩🇪' },
  { code: 'FR', name: 'France', emoji: '🇫🇷' },
  { code: 'JP', name: 'Japan', emoji: '🇯🇵' },
  { code: 'BR', name: 'Brazil', emoji: '🇧🇷' },
  { code: 'ZA', name: 'South Africa', emoji: '🇿🇦' }
];

const FAKE_BODIES = [
  // Weather / General
  'Authorities are monitoring conditions closely as models indicate shifting wind patterns throughout the afternoon.',
  'Commuters are advised to plan for minor delays as passing showers move across the metro area.',
  'Meteorologists say the pressure system will stabilize overnight, bringing clearer skies by morning.',
  'Residents reported brief bursts of rain mixed with sun, a typical spring pattern for the region.',
  'Officials confirmed that emergency teams remain on standby as weather advisories remain in effect.',
  'Traffic slowed in several areas as wet roads and reduced visibility impacted the evening commute.',
  'Forecasters noted that temperatures are expected to remain near seasonal averages through the week.',
  'Local authorities urged citizens to stay informed through official updates and public announcements.',

  // Sports
  'The home team secured a narrow victory after a late goal shifted momentum in the final minutes.',
  'Officials confirmed the match will go ahead as scheduled following a routine safety inspection.',
  'The coach praised the squad’s discipline and teamwork during the post-match press conference.',
  'Fans turned up in large numbers as the rivalry match delivered a tense and competitive atmosphere.',
  'Injury concerns remain as the star player awaits further medical assessment.',
  'Analysts noted improved defensive organization compared to the team’s previous outing.',
  'The tournament schedule was released, with opening matches set for later this month.',
  'Selectors hinted at possible lineup changes ahead of the upcoming series.',

  // Movies & Entertainment
  'The film opened to strong box office numbers, driven by positive word-of-mouth from early audiences.',
  'Critics praised the lead performance, calling it one of the actor’s most compelling roles to date.',
  'The movie’s soundtrack has quickly gained traction across streaming platforms.',
  'Producers confirmed that discussions for a sequel are underway following the film’s success.',
  'Fans gathered for the premiere as the cast walked the red carpet amid high expectations.',
  'Early reviews highlighted impressive visual effects and a tightly paced storyline.',
  'The director revealed that the project was inspired by real-life events and personal experiences.',
  'Industry observers noted a growing trend of mid-budget films performing strongly at theaters.'
];


// Pure function to derive news metadata - moved outside component
const deriveNewsMeta = (message) => {
  const seed = (message?.id || '') + String(message?.timestamp?.seconds || '') + (message?.senderId || '');
  let sum = 0;
  for (let i = 0; i < seed.length; i++) sum = (sum * 31 + seed.charCodeAt(i)) >>> 0;

  const source = SOURCES[sum % SOURCES.length];
  const author = AUTHORS[(sum >> 3) % AUTHORS.length];
  const country = COUNTRIES[(sum >> 5) % COUNTRIES.length];
  const imageSeed = (sum % 10000) + 1;
  const imageUrl = `https://picsum.photos/seed/${imageSeed}/800/400`;

  const basis = (message?.id || '') + (message?.content || '') + (message?.timestamp?.seconds || 0);
  let s = 0;
  for (let i = 0; i < String(basis).length; i++) s += String(basis).charCodeAt(i);
  const headline = HEADLINES[s % HEADLINES.length];
  const fakeBody = FAKE_BODIES[(sum >> 7) % FAKE_BODIES.length];

  return { source, author, country, imageUrl, headline, fakeBody };
};

// Helper to get fake news title
const getFakeNewsTitle = (message) => {
  const basis = (message?.id || '') + (message?.content || '') + (message?.timestamp?.seconds || 0);
  let sum = 0;
  for (let i = 0; i < String(basis).length; i++) sum += String(basis).charCodeAt(i);
  return HEADLINES[sum % HEADLINES.length];
};

const Chat = () => {
  const { friendId } = useParams();
  const navigate = useNavigate();
  const [friend, setFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [legacyKeysByUserId, setLegacyKeysByUserId] = useState({});
  const [isDecrypted, setIsDecrypted] = useState(false);
  const [decryptionTimer, setDecryptionTimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [puzzleStep1, setPuzzleStep1] = useState(false);
  const [puzzleStep2, setPuzzleStep2] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // { id, senderId }
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsArticles, setNewsArticles] = useState([]);
  const uploadInputRef = useRef(null);
  const bellHoldTimerRef = useRef(null);
  const imageHoldTimerRef = useRef(null);
  const modalBodyRef = useRef(null);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Store unsubscribe function in ref to avoid stale closures
  const unsubscribeRef = useRef(null);
  const cleanupIntervalRef = useRef(null);

  useEffect(() => {
    loadFriendData();

    const setup = async () => {
      // Cleanup previous listener if exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      unsubscribeRef.current = await loadMessages();
    };
    setup();

    // setupMessageListener is empty, removed redundant call
    cleanupIntervalRef.current = setupAutoCleanup();

    return () => {
      if (decryptionTimer) {
        clearTimeout(decryptionTimer);
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (cleanupIntervalRef.current) {
        cleanupIntervalRef.current();
        cleanupIntervalRef.current = null;
      }
    };
  }, [friendId]);

  // Focus modal body when upload modal opens
  useEffect(() => {
    if (showUploadModal && modalBodyRef.current) {
      setTimeout(() => {
        modalBodyRef.current?.focus();
      }, 100);
    }
  }, [showUploadModal]);

  // Global paste listener for when modal is open
  useEffect(() => {
    const handleGlobalPaste = async (e) => {
      if (showUploadModal) {
        console.log('Global paste detected');
        e.preventDefault(); // Prevent default paste behavior
        const items = e.clipboardData?.items;
        if (!items || !encryptionKey) {
          console.log('No items or no encryption key');
          return;
        }

        console.log('Processing clipboard items:', items.length);
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          console.log('Item type:', item.type);
          if (item.type.indexOf('image') !== -1) {
            const file = item.getAsFile();
            console.log('Got image file:', file);
            if (file) {
              try {
                const dataUrl = await compressImageToDataURL(file, 600, 0.6);
                console.log('Compressed image:', dataUrl ? 'success' : 'failed');
                if (dataUrl) {
                  setImagePreview(dataUrl);
                  setShowUploadModal(false); // Close modal to show preview
                }
              } catch (error) {
                console.error('Paste preview error:', error);
              }
            }
            break; // Only process first image
          }
        }
      }
    };

    if (showUploadModal) {
      document.addEventListener('paste', handleGlobalPaste);
      return () => document.removeEventListener('paste', handleGlobalPaste);
    }
  }, [showUploadModal, encryptionKey]);

  const loadFriendData = async () => {
    try {
      const friendDoc = await getDoc(doc(db, 'users', friendId));
      if (friendDoc.exists()) {
        setFriend(friendDoc.data());
      }
    } catch (error) {
      console.error('Error loading friend data:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const messagesRef = collection(db, 'messages');
      // Use single array-contains query (Firestore limitation)
      const q = query(
        messagesRef,
        where('participants', 'array-contains', auth.currentUser.uid)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messagesData = [];
        snapshot.forEach((docSnap) => {
          const messageData = docSnap.data();
          // Filter client-side to only include messages with this friend
          if (messageData.participants && messageData.participants.includes(friendId)) {
            messagesData.push({
              id: docSnap.id,
              ...messageData
            });
          }
        });

        // Sort messages by timestamp
        messagesData.sort((a, b) => {
          const aTs = typeof a.timestamp?.toMillis === 'function' ? a.timestamp.toMillis() : 0;
          const bTs = typeof b.timestamp?.toMillis === 'function' ? b.timestamp.toMillis() : 0;
          return aTs - bTs;
        });

        // Removed in-app notifications to prevent duplicate notifications
        // Sound notifications are handled globally in App.js

        // Limit to last 100 messages for performance
        const limitedMessages = messagesData.slice(-100);
        setMessages(limitedMessages);
        setLoading(false);
      }, (error) => {
        console.error('Snapshot listener error:', error);
        setLoading(false);
        toast.error('Failed to load messages. Please try again.');
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error loading messages:', error);
      setLoading(false);
    }
  };

  // Read receipts removed

  const setupAutoCleanup = useCallback(() => {
    // Clean up messages older than 2 days
    const cleanupInterval = setInterval(async () => {
      try {
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
        
        const messagesRef = collection(db, 'messages');
        // Use single array-contains query (Firestore limitation)
        const q = query(
          messagesRef,
          where('participants', 'array-contains', auth.currentUser.uid)
        );
        const snapshot = await getDocs(q);
        const deletePromises = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Filter client-side to only include messages with this friend
          if (data.participants && data.participants.includes(friendId)) {
            const ts = data.timestamp;
            const millis = typeof ts?.toMillis === 'function' ? ts.toMillis() : 0;
            if (millis > 0 && millis < Timestamp.fromDate(twoDaysAgo).toMillis()) {
              deletePromises.push(deleteDoc(docSnap.ref));
            }
          }
        });
        // Execute all deletions in parallel
        await Promise.all(deletePromises);
      } catch (error) {
        console.error('Auto cleanup error:', error);
      }
    }, 24 * 60 * 60 * 1000); // Run every 24 hours

    return () => clearInterval(cleanupInterval);
  }, [friendId]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !encryptionKey) return;

    const messageToSend = newMessage.trim();
    
    // Secret code check: message must end with "111"
    if (!messageToSend.endsWith('111')) {
      toast.error('Only admin is allowed to post in this news section.', { duration: 2000 });
      return;
    }

    // Remove the secret code suffix before sending
    const actualMessage = messageToSend.slice(0, -3).trim();
    if (!actualMessage) {
      toast.error('Only admin is allowed to post in this news section.', { duration: 2000 });
      return;
    }

    const replyToSend = replyTo;

    // Clear input immediately
    setNewMessage('');
    setReplyTo(null);

    try {
      const encryptedText = encryptMessage(actualMessage, encryptionKey);
      
      await addDoc(collection(db, 'messages'), {
        senderId: auth.currentUser.uid,
        receiverId: friendId,
        participants: [auth.currentUser.uid, friendId],
        content: encryptedText,
        timestamp: Timestamp.now(),
        isEncrypted: true,
        replyTo: replyToSend?.id || null
      });

    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      setNewMessage('');
      setReplyTo(null);
    }
  };

  // Helpers to convert/compress image to base64 and store inline
  const fileToDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const compressImageToDataURL = (file, maxDim = 600, quality = 0.6) => new Promise(async (resolve) => {
    try {
      const base = await fileToDataURL(file);
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        let q = quality;
        let dataUrl = canvas.toDataURL('image/jpeg', q);
        // Aim under ~600KB length; reduce further if needed
        for (let i = 0; i < 4 && dataUrl.length > 600000; i += 1) {
          q = Math.max(0.3, q - 0.1);
          dataUrl = canvas.toDataURL('image/jpeg', q);
        }
        resolve(dataUrl);
      };
      img.onerror = () => resolve(base);
      img.src = base;
    } catch {
      resolve(null);
    }
  });

  const handleFileSelected = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file || !encryptionKey) return;
      const currentUserId = auth.currentUser.uid;
      const dataUrl = await compressImageToDataURL(file, 600, 0.6);
      if (!dataUrl) return;
      await addDoc(collection(db, 'messages'), {
        senderId: currentUserId,
        receiverId: friendId,
        participants: [currentUserId, friendId],
        content: encryptMessage('[image]', encryptionKey),
        imageData: dataUrl,
        messageType: 'image',
        timestamp: Timestamp.now(),
      });
      e.target.value = '';
    } catch (_) {
      // silent fail to preserve disguise
    }
  };

  // Send weather alert notification
  const sendWeatherAlert = useCallback(async () => {
    if (!encryptionKey) return;

    const randomAlert = WEATHER_ALERTS[Math.floor(Math.random() * WEATHER_ALERTS.length)];

    try {
      const currentUserId = auth.currentUser.uid;
      await addDoc(collection(db, 'messages'), {
        senderId: currentUserId,
        receiverId: friendId,
        participants: [currentUserId, friendId],
        content: encryptMessage(randomAlert, encryptionKey),
        messageType: 'weather-alert',
        timestamp: Timestamp.now(),
      });

      // Play notification sound
      playBeep();

      toast.success('Weather alert sent!');
    } catch (error) {
      console.error('Weather alert send error:', error);
      toast.error('Failed to send weather alert');
    }
  }, [encryptionKey, friendId]);

  // Show in-app notification
  const showInAppNotification = (senderName, message) => {
    const notificationId = Date.now();
    const newNotification = {
      id: notificationId,
      senderName,
      message,
      timestamp: new Date(),
      type: 'message'
    };

    setNotifications(prev => [newNotification, ...prev]);

    // Play notification sound
    playBeep();

    // Auto-hide after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    }, 5000);
  };

  // Dismiss notification
  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Bell button handlers
  const handleBellMouseDown = (e) => {
    e.preventDefault();
    if (bellHoldTimerRef.current) clearTimeout(bellHoldTimerRef.current);

    // Start timer for long press
    bellHoldTimerRef.current = setTimeout(() => {
      setShowUploadModal(true);
      bellHoldTimerRef.current = null;
    }, 3000);
  };

  const handleBellMouseUp = (e) => {
    e.preventDefault();
    if (bellHoldTimerRef.current) {
      // If timer is still active, it was a click, not a long press
      clearTimeout(bellHoldTimerRef.current);
      bellHoldTimerRef.current = null;
      // Removed sendWeatherAlert() call for single click
    }
  };

  const handleBellMouseLeave = (e) => {
    e.preventDefault();
    if (bellHoldTimerRef.current) {
      clearTimeout(bellHoldTimerRef.current);
      bellHoldTimerRef.current = null;
    }
  };

  const handleBellTouchStart = (e) => {
    e.preventDefault();
    if (bellHoldTimerRef.current) clearTimeout(bellHoldTimerRef.current);

    // Start timer for long press
    bellHoldTimerRef.current = setTimeout(() => {
      setShowUploadModal(true);
      bellHoldTimerRef.current = null;
    }, 3000);
  };

  const handleBellTouchEnd = (e) => {
    e.preventDefault();
    if (bellHoldTimerRef.current) {
      // If timer is still active, it was a tap, not a long press
      clearTimeout(bellHoldTimerRef.current);
      bellHoldTimerRef.current = null;
      // Removed sendWeatherAlert() call for single tap
    }
  };

  // Handle paste from clipboard - show preview (fallback for modal-specific paste)
  const handlePaste = async (e) => {
    console.log('Modal paste event triggered', e);
    // The global paste listener handles most cases, this is just a fallback
  };

  // Handle file selection from modal - show preview
  const handleModalFileSelected = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file || !encryptionKey) return;
      const dataUrl = await compressImageToDataURL(file, 600, 0.6);
      if (dataUrl) {
        setImagePreview(dataUrl);
        setShowUploadModal(false); // Close modal to show preview
        e.target.value = '';
      }
    } catch (error) {
      console.error('File preview error:', error);
    }
  };

  // Confirm image upload from preview
  const confirmImageUpload = async () => {
    if (!imagePreview || !encryptionKey) return;

    setUploadLoading(true);
    try {
      const currentUserId = auth.currentUser.uid;
      await addDoc(collection(db, 'messages'), {
        senderId: currentUserId,
        receiverId: friendId,
        participants: [currentUserId, friendId],
        content: encryptMessage('[image]', encryptionKey),
        imageData: imagePreview,
        messageType: 'image',
        timestamp: Timestamp.now(),
      });
      setImagePreview(null);
      toast.success('Weather photo attached!');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadLoading(false);
    }
  };

  // Cancel image upload from preview
  const cancelImageUpload = () => {
    setImagePreview(null);
  };

  // Image long-press to enlarge for 5s
  const startImageHold = (url) => {
    if (!isDecrypted) return;
    if (imageHoldTimerRef.current) clearTimeout(imageHoldTimerRef.current);
    imageHoldTimerRef.current = setTimeout(() => setEnlargedImageUrl(url), 3000);
  };
  const cancelImageHold = () => {
    if (imageHoldTimerRef.current) {
      clearTimeout(imageHoldTimerRef.current);
      imageHoldTimerRef.current = null;
    }
  };

  // Puzzle-based decryption: drag sun -> cloud, then cloud -> rain
  const handleDragStart = (e, weather) => {
    e.dataTransfer.setData('text/plain', weather);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const startDecryptionViaPuzzle = () => {
    if (isDecrypted) return;
    setIsDecrypted(true);
    const timer = setTimeout(() => {
      setIsDecrypted(false);
      setDecryptionTimer(null);
      setPuzzleStep1(false);
      setPuzzleStep2(false);
      setEnlargedImageUrl(null);
    }, 10000);
    setDecryptionTimer(timer);
  };

  const handleDrop = (e, targetWeather) => {
    e.preventDefault();
    const dragged = e.dataTransfer.getData('text/plain');
    if (!puzzleStep1 && dragged === 'sun' && targetWeather === 'cloud') {
      setPuzzleStep1(true);
      return;
    }
    if (puzzleStep1 && !puzzleStep2 && dragged === 'cloud' && targetWeather === 'rain') {
      setPuzzleStep2(true);
      startDecryptionViaPuzzle();
    }
  };

  // Show delete confirmation modal
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  // Handle confirmed deletion
  const confirmDelete = async () => {
    setShowDeleteModal(false);
    try {
      const currentUserId = auth.currentUser?.uid;
      if (!currentUserId || !friendId) return;
      const messagesRef = collection(db, 'messages');
      // Use single array-contains query (Firestore limitation)
      const q = query(
        messagesRef,
        where('participants', 'array-contains', currentUserId)
      );
      const snapshot = await getDocs(q);
      let batch = writeBatch(db);
      let ops = 0;
      let deleted = 0;
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        // Filter client-side to only include messages with this friend
        if (data.participants && data.participants.includes(friendId)) {
          batch.delete(docSnap.ref);
          ops += 1;
          deleted += 1;
          if (ops >= 400) {
            batch.commit();
            batch = writeBatch(db);
            ops = 0;
          }
        }
      });
      if (ops > 0) await batch.commit();
      setMessages([]);
      toast.success(`Deleted ${deleted} message${deleted !== 1 ? 's' : ''}.`);
    } catch (error) {
      console.error('Clear conversation error:', error);
      toast.error('Failed to delete messages. Try again.');
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Cow button: immediately re-encrypt and return to news view
  const handleCowClick = () => {
    if (decryptionTimer) {
      clearTimeout(decryptionTimer);
      setDecryptionTimer(null);
    }
    setIsDecrypted(false);
    // Reset the puzzle so the sequence can be performed again
    setPuzzleStep1(false);
    setPuzzleStep2(false);
    setEnlargedImageUrl(null);
  };

  // No-op: the "Report" button acts as reply (disguised label)

  // Memoized getDisplayMessage to avoid recalculating on every render
  const getDisplayMessage = useCallback((message) => {
    if (!encryptionKey) {
      return getFakeNewsTitle(message);
    }

    // Handle weather alert messages
    if (message.messageType === 'weather-alert' && isDecrypted) {
      try {
        let plaintext = decryptMessage(message.content, encryptionKey);
        return plaintext; // Return the weather alert message directly
      } catch (error) {
        return '🌤️ Weather alert';
      }
    }

    if (isDecrypted) {
      try {
        let plaintext = decryptMessage(message.content, encryptionKey);
        if (typeof plaintext === 'string' && plaintext.trim().length > 0) {
          return plaintext;
        }
        // Fallback to legacy per-user keys for old messages
        const senderLegacyKey = legacyKeysByUserId[message.senderId];
        if (senderLegacyKey) {
          plaintext = decryptMessage(message.content, senderLegacyKey);
          if (typeof plaintext === 'string' && plaintext.trim().length > 0) {
            return plaintext;
          }
        }
        return '[Unable to decrypt]';
      } catch (error) {
        return generateGibberish(30);
      }
    } else {
      return getFakeNewsTitle(message);
    }
  }, [encryptionKey, isDecrypted, legacyKeysByUserId]);

  // Fetch small headline list for sidebar - with abort controller for cleanup
  const fetchNews = useCallback(async (signal) => {
    setNewsLoading(true);
    const trySource = async (fn) => {
      try {
        const items = await fn();
        if (Array.isArray(items) && items.length > 0) {
          if (!signal?.aborted) {
            setNewsArticles(items.slice(0, 8));
          }
          return true;
        }
      } catch (_) {}
      return false;
    };
    // Spaceflight News first (reliable CORS)
    const ok1 = await trySource(async () => {
      const r = await fetch('https://api.spaceflightnewsapi.net/v4/articles/?limit=8', { signal });
      if (!r.ok) return [];
      const j = await r.json();
      return (j?.results || []).map(a => ({
        title: a.title,
        imageUrl: a.image_url,
        content: a.summary,
        url: a.url
      }));
    });
    if (ok1 || signal?.aborted) { if (!signal?.aborted) setNewsLoading(false); return; }
    // Inshorts community (may fail)
    const ok2 = await trySource(async () => {
      const r = await fetch('https://inshorts.deta.dev/news?category=all', { signal });
      if (!r.ok) return [];
      const j = await r.json();
      return (j?.data || []).map(d => ({ title: d.title, imageUrl: d.imageUrl, content: d.content, url: d.readMoreUrl }));
    });
    if (ok2 || signal?.aborted) { if (!signal?.aborted) setNewsLoading(false); return; }
    // GNews if provided
    const gKey = process.env.REACT_APP_GNEWS_API_KEY;
    if (gKey) {
      const ok3 = await trySource(async () => {
        const r = await fetch(`https://gnews.io/api/v4/top-headlines?category=general&lang=en&max=8&apikey=${gKey}`, { signal });
        if (!r.ok) return [];
        const j = await r.json();
        return (j?.articles || []).map(a => ({ title: a.title, imageUrl: a.image, content: a.description, url: a.url }));
      });
      if (ok3 || signal?.aborted) { if (!signal?.aborted) setNewsLoading(false); return; }
    }
    if (!signal?.aborted) setNewsLoading(false);
  }, []);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (container) {
      try {
        container.scrollTo({ top: container.scrollHeight, behavior });
      } catch (_) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom('smooth');
  }, [messages, scrollToBottom]);

  // Memoize messages list for performance - now with stable dependencies
  const memoizedMessages = useMemo(() => {
    const currentUserId = auth.currentUser?.uid;
    const friendUsername = friend?.username || 'Source';
    const isLocked = !isDecrypted;

    return messages.map((message, index) => {
      const meta = deriveNewsMeta(message);
      const displayText = getDisplayMessage(message);
      const parent = message.replyTo ? messages.find((m) => m.id === message.replyTo) : null;
      const parentDisplay = parent ? getDisplayMessage(parent) : null;
      const realSender = message.senderId === currentUserId ? 'You' : friendUsername;

      return {
        ...message,
        meta,
        displayText,
        isLocked,
        parent,
        parentDisplay,
        realSender,
        // Limit animation delay to prevent performance issues with many messages
        animationDelay: index < 10 ? Math.min(index * 0.05, 0.5) : 0
      };
    });
  }, [messages, isDecrypted, friend?.username, getDisplayMessage]);

  useEffect(() => {
    // Retrieve or create a shared conversation key for both participants
    const ensureConversationKey = async () => {
      try {
        const currentUserId = auth.currentUser.uid;
        if (!currentUserId || !friendId) return;

        const conversationId = [currentUserId, friendId].sort().join('_');
        const conversationRef = doc(db, 'conversations', conversationId);
        const conversationSnap = await getDoc(conversationRef);

        if (conversationSnap.exists()) {
          const data = conversationSnap.data();
          if (data?.sharedKey) {
            setEncryptionKey(data.sharedKey);
            return;
          }
        }

        const newSharedKey = generateEncryptionKey();
        await setDoc(conversationRef, {
          participants: [currentUserId, friendId],
          sharedKey: newSharedKey,
          createdAt: Timestamp.now()
        }, { merge: true });
        setEncryptionKey(newSharedKey);
      } catch (error) {
        console.error('Error ensuring conversation key:', error);
      }
    };

    ensureConversationKey();
  }, [friendId]);

  useEffect(() => {
    // Load legacy per-user keys to support decrypting older messages
    const loadLegacyKeys = async () => {
      try {
        const currentUserId = auth.currentUser?.uid;
        if (!currentUserId || !friendId) return;
        const [meSnap, friendSnap] = await Promise.all([
          getDoc(doc(db, 'users', currentUserId)),
          getDoc(doc(db, 'users', friendId))
        ]);
        const map = {};
        if (meSnap.exists() && meSnap.data().encryptionKey) {
          map[currentUserId] = meSnap.data().encryptionKey;
        }
        if (friendSnap.exists() && friendSnap.data().encryptionKey) {
          map[friendId] = friendSnap.data().encryptionKey;
        }
        setLegacyKeysByUserId(map);
      } catch (error) {
        console.error('Error loading legacy keys:', error);
      }
    };
    loadLegacyKeys();
  }, [friendId]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchNews(abortController.signal);
    return () => abortController.abort();
  }, [fetchNews]);

  if (loading) {
    return (
      <div className="chat-loading">
        <div className="loading-spinner"></div>
        <p>Loading weather updates...</p>
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* Chat Header */}
      <div className="chat-header">
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="chat-info">
          <h3>Weather Updates from {friend?.username}</h3>
          <div className="weather-status">
            {isDecrypted ? (
              <Unlock size={14} />
            ) : (
              <Lock size={14} />
            )}
          </div>
        </div>
        
        {/* Decryption Puzzle */}
        <div className="puzzle-container">
          <div
            className={`weather sun ${puzzleStep1 ? 'completed' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, 'sun')}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'sun')}
          >
            ☀️
          </div>
          <div
            className={`weather cloud ${puzzleStep1 ? 'armed' : ''}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'cloud')}
            draggable
            onDragStart={(e) => handleDragStart(e, 'cloud')}
          >
            ☁️
          </div>
          <div
            className={`weather rain ${puzzleStep2 ? 'completed' : ''}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'rain')}
          >
            🌧️
          </div>
          {decryptionTimer && (
            <div className="timer-indicator">
              <Clock size={12} />
            </div>
          )}
        </div>
        <button type="button" className={`cow-btn ${isDecrypted ? 'active' : ''}`} onClick={handleCowClick} disabled={!isDecrypted}>🐮</button>
        <button type="button" className="clear-convo-btn" onClick={handleDeleteClick}>
          <Trash2 size={18} />
        </button>
      </div>

      {/* Main area: feed + sidebar */}
      <div className="chat-main">
        <div className="messages-container" ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>No weather bulletins yet. Submit a tip below.</p>
            </div>
          ) : (
            <div className="messages-list feed">
              {memoizedMessages.map((message) => {
              const { meta, displayText, isLocked, parent, parentDisplay, realSender, animationDelay } = message;
              return (
                <article
                  key={message.id}
                  className={`article-card ${message.messageType === 'weather-alert' ? 'weather-alert-message' : ''}`}
                  style={{ animationDelay: `${animationDelay}s` }}
                >
                  <img className="article-image" src={meta.imageUrl} alt="" loading="lazy" />
                  <div className="article-content">
                    <div className="article-meta">
                      <span className="article-source">{meta.source}</span>
                      <span className="article-time">
                        {(meta.country?.emoji || '🌍')} {(meta.country?.name || 'Global')} · {message.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {message.replyTo && (
                      <div className="article-reply-quote" onClick={() => { if (parent) { setReplyTo({ id: parent.id, senderId: parent.senderId }); inputRef.current && inputRef.current.focus(); } }}>
                        <span className="reply-label">In reply to</span>
                        <span className="reply-author">{parent?.senderId === auth.currentUser.uid ? 'You' : (friend?.username || 'Source')}</span>
                        <div className="reply-excerpt">{parentDisplay || 'Original update'}</div>
                      </div>
                    )}
                    <h2 className="article-title">{isLocked ? meta.headline : displayText}</h2>
                    {message.messageType === 'image' && (message.imageData || message.imageUrl) && isDecrypted ? (
                      <div className="thumb-wrap">
                        <img
                          src={message.imageData || message.imageUrl}
                          alt="attachment"
                          className="thumb"
                          onMouseDown={() => startImageHold(message.imageData || message.imageUrl)}
                          onMouseUp={cancelImageHold}
                          onMouseLeave={cancelImageHold}
                          onTouchStart={() => startImageHold(message.imageData || message.imageUrl)}
                          onTouchEnd={cancelImageHold}
                        />
                      </div>
                    ) : (
                      isLocked && <p className="article-body">{meta.fakeBody}</p>
                    )}
                    <div className="article-footer">
                      <span className="article-author">By {realSender}</span>
                      <span className="article-paper">{meta.source}</span>
                      <button
                        type="button"
                        className="reply-btn"
                        onClick={() => { setReplyTo({ id: message.id, senderId: message.senderId }); inputRef.current && inputRef.current.focus(); }}
                        title="Report"
                        aria-label="Report"
                      >
                        <Flag size={14} />
                      </button>
                    </div>
                  </div>
                </article>
              );
              })}
              <div ref={messagesEndRef} style={{ height: 1 }} />
            </div>
          )}
        </div>
        <aside className="news-sidebar">
          <div className="news-mini-header">Latest Headlines</div>
          <div className="news-mini-list">
            {newsLoading ? (
              <div className="news-mini-skeletons">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="news-mini-card skeleton" />
                ))}
              </div>
            ) : (
              newsArticles.map((n, idx) => (
                <a key={idx} href={n.url || '#'} target="_blank" rel="noreferrer" className="news-mini-card">
                  {n.imageUrl && <img src={n.imageUrl} alt="headline" loading="lazy" />}
                  <div className="mini-content">
                    <div className="mini-title">{n.title}</div>
                    {n.content && <div className="mini-desc">{n.content}</div>}
                  </div>
                </a>
              ))
            )}
          </div>
        </aside>
      </div>

      {/* Tip Submission (disguised input) */}
      <form onSubmit={sendMessage} className={`message-input-container ${replyTo ? 'has-reply' : ''}`}>
        <input type="file" accept="image/*" ref={uploadInputRef} onChange={handleFileSelected} style={{ display: 'none' }} />
        {replyTo && (
          <div className="reply-preview">
            <div className="reply-preview-header">
              <span className="replying-to">Replying to {replyTo.senderId === auth.currentUser.uid ? 'You' : (friend?.username || 'Source')}</span>
              <button type="button" className="reply-cancel" onClick={() => setReplyTo(null)}>×</button>
            </div>
            <div className="reply-preview-body">
              {(() => {
                const parent = messages.find((m) => m.id === replyTo.id);
                return parent ? getDisplayMessage(parent) : 'Original update';
              })()}
            </div>
          </div>
        )}
        <div className="message-input-row">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Submit a weather tip or breaking update..."
            disabled={!encryptionKey}
            ref={inputRef}
          />
          <button 
          type="submit" 
          className="send-btn"
          disabled={!newMessage.trim() || !encryptionKey}
          aria-label="Send"
        >
          <span className="owl-icon" role="img" aria-label="owl">🦉</span>
        </button>
        <button
          type="button"
          className="bell-btn"
          onMouseDown={handleBellMouseDown}
          onMouseUp={handleBellMouseUp}
          onMouseLeave={handleBellMouseLeave}
          onTouchStart={handleBellTouchStart}
          onTouchEnd={handleBellTouchEnd}
          aria-label="Request weather update or upload photo"
        >
          🔔
        </button>
        </div>
      </form>
      {enlargedImageUrl && (
        <div className="image-overlay" onClick={() => setEnlargedImageUrl(null)}>
          <div className="image-overlay-inner">
            <button className="image-close" onClick={() => setEnlargedImageUrl(null)} aria-label="Close">✕</button>
            <img src={enlargedImageUrl} alt="attachment" />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h3>Erase All Weather Data & Updates</h3>
            </div>
            <div className="delete-modal-body">
              <p>This action cannot be undone. All weather bulletins, updates, and forecast information will be permanently deleted.</p>
            </div>
            <div className="delete-modal-actions">
              <button className="delete-cancel-btn" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="delete-confirm-btn" onClick={confirmDelete}>
                Delete Weather Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3>Attach Weather Photo</h3>
              <button
                className="upload-modal-close"
                onClick={() => setShowUploadModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div
              className="upload-modal-body"
              onPaste={handlePaste}
              tabIndex="0"
              style={{ outline: 'none' }}
              ref={modalBodyRef}
            >
              <div className="upload-options">
                <div className="upload-option">
                  <label htmlFor="file-upload" className="upload-option-btn">
                    📁 Select from Device
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleModalFileSelected}
                    style={{ display: 'none' }}
                  />
                </div>
                <div className="upload-option">
                  <div className="upload-option-btn paste-instruction">
                    📋 Paste Image (Ctrl+V)
                  </div>
                  <p className="paste-hint">Copy an image and paste it here</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <div className="image-preview-overlay" onClick={cancelImageUpload}>
          <div className="image-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-preview-header">
              <h3>Preview Weather Photo</h3>
              <button
                className="image-preview-close"
                onClick={cancelImageUpload}
                aria-label="Cancel"
              >
                ×
              </button>
            </div>
            <div className="image-preview-body">
              <img
                src={imagePreview}
                alt="Preview"
                className="image-preview-img"
              />
            </div>
            <div className="image-preview-actions">
              <button
                className="image-preview-cancel"
                onClick={cancelImageUpload}
                disabled={uploadLoading}
              >
                Cancel
              </button>
              <button
                className="image-preview-confirm"
                onClick={confirmImageUpload}
                disabled={uploadLoading}
              >
                {uploadLoading ? (
                  <>
                    <div className="upload-spinner"></div>
                    Uploading...
                  </>
                ) : (
                  'Send Photo'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Overlay */}
      {notifications.length > 0 && (
        <div className="notification-overlay">
          {notifications.map((notification) => (
            <div key={notification.id} className="notification-card">
              <div className="notification-header">
                <div className="notification-avatar">
                  {notification.senderName.charAt(0).toUpperCase()}
                </div>
                <div className="notification-info">
                  <div className="notification-sender">{notification.senderName}</div>
                  <div className="notification-time">
                    {notification.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <button
                  className="notification-close"
                  onClick={() => dismissNotification(notification.id)}
                  aria-label="Close notification"
                >
                  ×
                </button>
              </div>
              <div className="notification-message">
                {notification.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Chat;
